import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { memberClient } from 'src/config/Apollo';
import { CALENDAR_QUERY } from 'src/pages/SessionsPage';

import { Dispatch } from '../config/Store';
import { getCurrentUser } from '../selectors/userSelectors';
import { colors } from '../themes/colors';
import MHButton from './MHButton';
import { updateAvailableHoursMutation } from './Sessions/AvailableHoursModal';

const useStyles = makeStyles({
  title: {
    fontFamily: 'Avenir-Black',
    fontSize: 18,
    color: colors.black4,
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    marginRight: 20,
    backgroundColor: colors.gray1,
    color: colors.black1,
    '&:hover': {
      backgroundColor: colors.gray6,
    },
  },
});

export const UpdateTimezoneModal: React.FC = () => {
  const classes = useStyles();
  const snackbar = useSnackbar();

  const [modalOpen, setModalOpen] = useState(false);
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useDispatch<Dispatch>();

  const closeDialog = () => {
    setModalOpen(false);
    // If they dismiss the modal, store the timezone they've dismissed and don't prompt
    // for a change to that timezone again.
    sessionStorage.setItem('timezoneDismissed', timezoneGuess);
  };

  // QUICKFIX - https://joinmodernhealth.atlassian.net/browse/CC-1632
  // this is to re-save the working hours which will result in applying a new time zone to the availability
  // ideally this should be handled on the BE
  const updateWorkHours = async () => {
    const response = await memberClient.query({
      query: CALENDAR_QUERY,
      fetchPolicy: 'network-only',
    });

    const workHours = response?.data?.availableHours;

    if (workHours?.length < 1) {
      return;
    }

    const availableHours = workHours.map((range) => ({
      ...range,
      start: moment(range.start, [moment.ISO_8601, 'HH:mm A']).format('HH:mm'),
      end: moment(range.end, [moment.ISO_8601, 'HH:mm A']).format('HH:mm'),
    }));

    memberClient.mutate({
      mutation: updateAvailableHoursMutation,
      variables: { availableHours },
    });
  };

  const handleUpdate = () => {
    dispatch.user
      .updateUserTimezone({ timezone: timezoneGuess })
      .then(() => {
        updateWorkHours();
        closeDialog();
      })
      .catch(() => {
        closeDialog();
        snackbar.enqueueSnackbar('Could not update time zone', { variant: 'error' });
      });
  };

  const timezoneGuess = momentTimezone.tz.guess();

  useEffect(() => {
    const timezoneGuessValid = typeof timezoneGuess == 'string' && timezoneGuess !== '';
    const dismissed = sessionStorage.getItem('timezoneDismissed');

    if (
      timezoneGuessValid &&
      currentUser.id &&
      currentUser.localTimezone !== timezoneGuess &&
      dismissed !== timezoneGuess
    ) {
      setModalOpen(true);
    }
  }, [currentUser, timezoneGuess]);

  return (
    <Dialog
      open={modalOpen}
      onClose={closeDialog}
      aria-labelledby="form-dialog-title"
      maxWidth="sm"
    >
      <DialogTitle id="form-dialog-title" className={classes.dialogTitle} disableTypography>
        <Typography className={classes.title}>Timezone Change Detected</Typography>
        <IconButton aria-label="Close" onClick={closeDialog}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {currentUser.localTimezone ? (
          <Typography>
            We've detected that your timezone has changed from "{currentUser.localTimezone}" to "
            {timezoneGuess}". Would you like to update it?
          </Typography>
        ) : (
          <Typography>
            We've detected that your timezone is "{timezoneGuess}". Would you like to set it?
          </Typography>
        )}
        <div className={classes.buttonRow}>
          <MHButton extraClasses={[classes.cancelButton]} onClick={closeDialog}>
            Not Now
          </MHButton>
          <MHButton onClick={handleUpdate}>Save</MHButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
